import { InputOptionAttributes } from 'components/Shared/Form/InputSelect/option';
import { ChangeEvent, Component, FocusEvent } from 'react';
import { debounceP } from 'utils';
import { EventWithDataHandler, Field } from 'redux-form';
import {
  RenderLabeledInputSelect,
  RenderLabeledInputSelectProps
} from 'components/Shared/Form/inputs';
import { Validator } from 'components/Shared/Form';
import { SuggestedAddress } from 'models/SuggestedAddress';
import { formatAddress, formatZipCode } from 'views/CheckoutPage/selector';

export const addressFormat = (a: SuggestedAddress) => {
  const street = `${formatAddress(a)}, ${formatZipCode(a)}`.replace('(s)s+', '$1');
  return {
    key: street,
    label: street,
    value: a
  };
};
export const zipFormat = (a: SuggestedAddress) => {
  const zip = formatZipCode(a).replace('(s)s+', '$1');
  return {
    key: zip,
    label: zip,
    value: a
  };
};
export const zipParser = (input: string) => (input ? input.replace(/\D/g, '') : '');

interface State<T> {
  preventBlur: boolean;
  isOpen: boolean;
  options?: InputOptionAttributes<T>[];
  isLoading: boolean;
  isFocused: boolean;
}
interface Props<T> {
  name: string;
  className: string;
  validate: Validator;
  label: string;
  onSelect: (item: T) => void;
  onBlur: EventWithDataHandler<FocusEvent>;
  format: (item: T) => InputOptionAttributes<T>;
  onChange: (value: string) => Promise<T[]>;
  inputClassName?: string;
  props?: Partial<RenderLabeledInputSelectProps>;
  parse?: (input: string) => string;
}
export default class SuggestionField<T> extends Component<Props<T>, State<T>> {
  public state = {
    isLoading: false,
    preventBlur: false,
    isOpen: false,
    options: undefined,
    isFocused: false
  };
  private DELAY = 300;
  private debounced: (v: string) => Promise<T[]>;

  constructor(props: Props<T>) {
    super(props);
    this.debounced = debounceP(props.onChange, this.DELAY);
  }

  public render() {
    return (
      <Field
        name={this.props.name}
        validate={this.props.validate}
        className={this.props.className}
        isMenuOpen={this.isMenuOpen()}
        onMenuOpen={this.onMenuOpen}
        onMenuClose={this.onMenuClose}
        onSelect={this.onSelect}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onChange={this.onChange}
        options={this.state.options}
        isLoading={this.state.isLoading}
        component={RenderLabeledInputSelect}
        props={{
          customLabel: this.props.label,
          inputClassName: this.props.inputClassName,
          ...this.props.props
        }}
        parse={this.props.parse}
      />
    );
  }
  private onBlur: EventWithDataHandler<FocusEvent> = (e, n, p, name) => {
    e && e.preventDefault();
    this.setState({ isFocused: false });
    this.props.onBlur(e, n, p, name);
  };
  private onFocus = () => this.setState({ isFocused: true });
  private onMenuOpen = () => this.setState({ isOpen: true });
  private onMenuClose = () => this.setState({ isOpen: false });
  private isMenuOpen = () => this.state.isOpen;
  private onSelect: (value: InputOptionAttributes<T>) => void = item => {
    this.setState({ options: undefined });
    this.props.onSelect(item.value);
  };
  private onChange: EventWithDataHandler<ChangeEvent> = (_e, value: string) => {
    const VALUE_THRESHOLD = 3;
    if (value.length < VALUE_THRESHOLD || !this.state.isFocused) {
      return;
    }
    this.setState({ isLoading: true });
    void this.debounced(value)
      .then(adrs => adrs?.map(this.props.format))
      .then(options => this.setState({ options, isLoading: false }));
  };
}
